import { toast } from 'sonner';
import i18n from '../lib/i18n';
import { ApiError } from './errors';
import { RequestTimeoutError } from './client';

export type ErrorUxStrategy =
    | 'TOAST'
    | 'INLINE'
    | 'REDIRECT_LOGIN'
    | 'REDIRECT_MFA_SETUP'
    | 'IGNORE_HANDLED_BY_FORM';

// TD-WEBAPP-004: codes faltantes del backend no mapeados. Ver TECH_DEBT.md.
const STRATEGY_BY_CODE: Record<string, ErrorUxStrategy> = {
    SESSION_EXPIRED: 'REDIRECT_LOGIN',
    MFA_REQUIRED: 'REDIRECT_MFA_SETUP',
    // Lo pintan LoginForm y MfaChallengeForm; aquí no se duplica el aviso.
    INVALID_CREDENTIALS: 'IGNORE_HANDLED_BY_FORM',
    VALIDATION_ERROR: 'INLINE',
    SCOPE_INVALID_FOR_ROLE: 'INLINE',
    PERMISSION_DENIED: 'TOAST',
    IDEMPOTENCY_CONFLICT: 'TOAST',
    SECURITY_POLICY_VIOLATION: 'TOAST',
    TENANT_SUSPENDED: 'REDIRECT_LOGIN',
    TENANT_NOT_FOUND: 'TOAST',
    TENANT_ALREADY_EXISTS: 'INLINE',
    USER_NOT_FOUND: 'TOAST',
    USER_ALREADY_EXISTS: 'INLINE',
    ROLE_NOT_FOUND: 'TOAST',
    // Destino de la asignación (entidad de nivel) ya no existe (borrado entre el list y el submit).
    LEVEL_ENTITY_NOT_FOUND: 'TOAST',
    MESSAGES_BUSINESS_ERROR: 'TOAST',
    AUDIT_WRITE_ERROR: 'TOAST',
    INTERNAL_ERROR: 'TOAST',
    // Usuarios de API: los de IPs se corrigen en el campo; el resto se avisa con toast.
    API_USER_ALREADY_CREATED: 'TOAST',
    API_USER_NOT_SUPPORTED: 'TOAST',
    NOT_AN_API_USER: 'TOAST',
    API_USER_ROLE_FORBIDDEN: 'TOAST',
    API_USER_SCOPE_FORBIDDEN: 'TOAST',
    API_USER_MANAGE_GRANT_FORBIDDEN: 'TOAST',
    IP_ALLOWLIST_REQUIRED: 'INLINE',
    INVALID_IP_ALLOWLIST: 'INLINE',
    IP_ALLOWLIST_TOO_BROAD: 'INLINE',
    IP_ALLOWLIST_TOO_LONG: 'INLINE',
    IP_ALLOWLIST_NOT_PUBLIC: 'INLINE',
};

// Código del back → clave de errors.json. Lo que no está aquí cae a `errors:unknown`.
const MESSAGE_KEY_BY_CODE: Record<string, string> = {
    SESSION_EXPIRED: 'sessionExpired',
    NO_SESSION: 'sessionExpired',
    UNAUTHENTICATED: 'sessionExpired',
    MFA_REQUIRED: 'mfaRequired',
    INVALID_CREDENTIALS: 'invalidCredentials',
    VALIDATION_ERROR: 'validationError',
    SCOPE_INVALID_FOR_ROLE: 'scopeInvalidForRole',
    PERMISSION_DENIED: 'permissionDenied',
    IDEMPOTENCY_CONFLICT: 'idempotencyConflict',
    SECURITY_POLICY_VIOLATION: 'securityPolicyViolation',
    TENANT_SUSPENDED: 'tenantSuspended',
    TENANT_NOT_FOUND: 'tenantNotFound',
    TENANT_ALREADY_EXISTS: 'tenantAlreadyExists',
    USER_NOT_FOUND: 'userNotFound',
    USER_ALREADY_EXISTS: 'userAlreadyExists',
    ROLE_NOT_FOUND: 'roleNotFound',
    ROLE_ALREADY_EXISTS: 'roleAlreadyExists',
    ROLE_IN_USE: 'roleInUse',
    ROLE_ARCHIVED: 'roleArchived',
    SYSTEM_PERMISSION_IMMUTABLE: 'systemPermissionImmutable',
    LEVEL_ENTITY_NOT_FOUND: 'levelEntityNotFound',
    PARENT_LEVEL_ENTITY_NOT_FOUND: 'levelEntityNotFound',
    LEVEL_ENTITY_HAS_CHILDREN: 'levelEntityHasChildren',
    MESSAGES_BUSINESS_ERROR: 'messagesBusinessError',
    AUDIT_WRITE_ERROR: 'auditWriteError',
    IDENTIFIER_TAKEN: 'identifierTaken',
    SELF_REGISTRATION_IDENTIFIER_TAKEN: 'identifierTaken',
    INVALID_IDENTIFIER: 'invalidIdentifier',
    SELF_REGISTRATION_OTP_INVALID: 'otpInvalid',
    PASSWORD_RESET_OTP_INVALID: 'otpInvalid',
    SELF_REGISTRATION_OTP_EXPIRED: 'otpExpired',
    PASSWORD_RESET_OTP_EXPIRED: 'otpExpired',
    SELF_REGISTRATION_OTP_ALREADY_USED: 'otpAlreadyUsed',
    PASSWORD_RESET_OTP_ALREADY_USED: 'otpAlreadyUsed',
    SELF_REGISTRATION_EXPIRED: 'selfRegistrationExpired',
    SELF_REGISTRATION_INVALID_STATE: 'selfRegistrationExpired',
    SELF_REGISTRATION_ALREADY_COMPLETED: 'selfRegistrationAlreadyCompleted',
    SELF_REGISTER_NOT_ALLOWED: 'selfRegisterNotAllowed',
    PASSWORD_RESET_SESSION_INVALID: 'passwordResetSessionInvalid',
    EMAIL_IMMUTABLE: 'emailImmutable',
    SMS_MFA_NO_PHONE: 'smsMfaNoPhone',
    SMS_MFA_PHONE_MISSING: 'smsMfaNoPhone',
    BATCH_LIMIT_EXCEEDED: 'batchLimitExceeded',
    COGNITO_INVALID_OTP: 'mfaInvalidOtp',
    COGNITO_RATE_LIMITED: 'cognitoRateLimited',
    COGNITO_INVALID_PASSWORD: 'invalidPassword',
    COGNITO_USER_ALREADY_EXISTS: 'userAlreadyExists',
    COGNITO_USER_NOT_FOUND: 'userNotFound',
    API_USER_ALREADY_CREATED: 'apiUserAlreadyCreated',
    API_USER_NOT_SUPPORTED: 'apiUserNotSupported',
    NOT_AN_API_USER: 'notAnApiUser',
    API_USER_ROLE_FORBIDDEN: 'apiUserRoleForbidden',
    API_USER_SCOPE_FORBIDDEN: 'apiUserScopeForbidden',
    API_USER_MANAGE_GRANT_FORBIDDEN: 'apiUserManageGrantForbidden',
    API_USER_SETUP_INCOMPLETE: 'apiUserSetupIncomplete',
    API_APP_CLIENT_MISSING: 'apiAppClientMissing',
    IP_ALLOWLIST_REQUIRED: 'ipAllowlistRequired',
    INVALID_IP_ALLOWLIST: 'invalidIpAllowlist',
    IP_ALLOWLIST_TOO_BROAD: 'ipAllowlistTooBroad',
    IP_ALLOWLIST_TOO_LONG: 'ipAllowlistTooLong',
    IP_ALLOWLIST_NOT_PUBLIC: 'ipAllowlistNotPublic',
};

// Estrategias en las que handleApiError ya muestra el aviso.
const TOASTED_STRATEGIES: ReadonlySet<ErrorUxStrategy> = new Set(['TOAST', 'INLINE']);

function resolveStrategy(error: ApiError): ErrorUxStrategy {
    const code = error.code;
    // Rechazo a nivel API Gateway / authorizer: el body es `{message:"Forbidden"}` (sin el
    // wire-shape Fiado), así que `code` viene undefined. Un 401/403 sin code = sesión inválida
    // o expirada → re-login. (Un 403 con code, p.ej. PERMISSION_DENIED, NO entra aquí.)
    if (!code) {
        return error.httpStatus === 401 || error.httpStatus === 403 ? 'REDIRECT_LOGIN' : 'TOAST';
    }
    if (code === 'NO_SESSION') return 'REDIRECT_LOGIN';
    if (Object.prototype.hasOwnProperty.call(STRATEGY_BY_CODE, code)) {
        return STRATEGY_BY_CODE[code];
    }
    if (code.startsWith('COGNITO_')) {
        return code === 'COGNITO_INVALID_OTP' ? 'INLINE' : 'TOAST';
    }
    return 'TOAST';
}

/** Mensaje en español para el usuario: nunca el `msg` crudo del back ni un texto vacío. */
export function resolveErrorMessage(error: unknown): string {
    if (error instanceof RequestTimeoutError) return error.message;
    if (!(error instanceof ApiError) || !error.code) return i18n.t('errors:unknown');
    const key = MESSAGE_KEY_BY_CODE[error.code] ?? 'unknown';
    return i18n.t(`errors:${key}`);
}

/** Muestra el aviso según la estrategia del code y la devuelve al llamador. */
export function handleApiError(error: unknown): ErrorUxStrategy {
    const strategy = error instanceof ApiError ? resolveStrategy(error) : 'TOAST';
    if (TOASTED_STRATEGIES.has(strategy)) {
        toast.error(resolveErrorMessage(error));
    }
    return strategy;
}

/** Como handleApiError, pero siempre muestra un aviso (para pantallas sin redirección propia). */
export function notifyApiError(error: unknown): ErrorUxStrategy {
    const strategy = handleApiError(error);
    if (!TOASTED_STRATEGIES.has(strategy)) {
        toast.error(resolveErrorMessage(error));
    }
    return strategy;
}
